/**
 * ==========================================================================
 *  REPLAY FILE FORMAT  ("LNIA")
 * ==========================================================================
 *  A replay is the complete input history of one attempt: for every fixed
 *  simulation step, which buttons were held and where the mouse was pointing.
 *  Nothing else. There is no position track, no velocity, no time - all of
 *  that is *derived* by re-simulating, which is exactly the point.
 *
 *  Layout:
 *
 *    "LNIA"          4  magic
 *    version         u16 LE
 *    headerLength    u16 LE
 *    header          headerLength bytes of UTF-8 JSON
 *    frameCount      u32 LE
 *    frames          variable, see below
 *
 *  Frames are delta-coded and run-length compressed:
 *
 *    tag < 0x80  ->  tag is the button mask, followed by zig-zag varints for
 *                    the yaw and pitch delta against the previous frame.
 *    tag == 0x80 ->  a varint follows: repeat the previous frame that many
 *                    more times. Frames carrying an edge-triggered bit - E, F
 *                    or one of the three boxes at the muurikka - are never
 *                    run-length coded: a key press is an event, and repeating
 *                    it would misrepresent what the player did.
 *    tag == 0x81 ->  escape. A varint button mask follows, then the same two
 *                    deltas. This is how a frame with a bit above 0x7f gets
 *                    written, which in practice means the handful of steps a
 *                    run presses `1`, `2` or `3` on. Everything else takes the
 *                    byte-per-frame path above.
 *
 *  A 50 second run is ~7-15 kB, most of which is the mouse. That is small
 *  enough to POST inline and to keep forever.
 *
 *  ------------------------------------------------------------------------
 *   VERSION 2
 *  ------------------------------------------------------------------------
 *  Version 1 had seven button bits and no escape tag, because seven bits was
 *  everything the game had. Breakfast added three more (see `BUTTON`), and a
 *  format that silently truncated them would drop exactly the key presses that
 *  decide whether a run gets through the door. There is no reader for version 1
 *  and there does not need to be one: the tuning that came with the boxes moved
 *  `simulationFingerprint()`, so every replay written before this one is
 *  already unscoreable. See the root CLAUDE.md.
 * ==========================================================================
 */

import { isSeed } from '../sim/seed';
import { SIMULATION_HZ } from '../config/gameplay';
import {
  BUTTON_MASK,
  EVENT_MASK,
  FAST_BUTTON_MASK,
  clampPitchUnits,
  wrapYawUnits,
  type StepInput,
} from '../sim/StepInput';

export const REPLAY_MAGIC = 'LNIA';
// Version 3 requires an explicit layout seed; version 2 headers lack one.
export const REPLAY_VERSION = 3;

/** Repeat the previous frame. Followed by a varint count. */
const TAG_REPEAT = 0x80;
/** A frame whose buttons do not fit in a tag byte. Followed by a varint mask. */
const TAG_WIDE = 0x81;

/** 20 minutes. Long enough for the worst run anyone will ever submit. */
export const MAX_REPLAY_FRAMES = SIMULATION_HZ * 60 * 20;
/** Header JSON is small by construction; anything larger is not ours. */
export const MAX_HEADER_BYTES = 2048;

export interface ReplayHeader {
  /** Independent per-attempt PIN seed. Omitted by deterministic offline fixtures. */
  pinSeed?: number;
  /** Layout seed, fixed before the attempt starts. */
  seed: number;
  /**
   * The server-issued run id this attempt was played under. Empty for a run
   * recorded offline - such a replay is still valid, it just cannot be ranked.
   */
  runId: string;
  /**
   * Hash of every tuning constant and every collider that affects the result.
   * The server refuses to validate a replay whose physics are not its own.
   * See src/sim/fingerprint.ts.
   */
  fingerprint: string;
  levelName: string;
  /** The dose. The category is derived from this server-side, never trusted. */
  alcoholPromille: number;
  drugsPercent: number;
  simulationHz: number;
  /**
   * The look sensitivity in force during the run. Not a gameplay value - it is
   * here so the analyser knows what the mouse-pixel grid should look like.
   */
  mouseSensitivity: number;
  /** Client wall clock at the start of the run. Metadata; never trusted. */
  startedAt: number;
}

export interface Replay {
  header: ReplayHeader;
  frames: StepInput[];
}

export class ReplayFormatError extends Error {}

/* ------------------------------------------------------------- varints ---- */

function writeVarint(out: number[], value: number): void {
  let v = value >>> 0;
  while (v >= 0x80) {
    out.push((v & 0x7f) | 0x80);
    v >>>= 7;
  }
  out.push(v);
}

function zigzag(value: number): number {
  return ((value << 1) ^ (value >> 31)) >>> 0;
}

function unzigzag(value: number): number {
  return (value >>> 1) ^ -(value & 1);
}

class Reader {
  private offset = 0;

  constructor(private readonly bytes: Uint8Array) {}

  get position(): number {
    return this.offset;
  }

  get remaining(): number {
    return this.bytes.length - this.offset;
  }

  u8(): number {
    if (this.offset >= this.bytes.length) throw new ReplayFormatError('replay ended mid-frame');
    return this.bytes[this.offset++];
  }

  u16(): number {
    return this.u8() | (this.u8() << 8);
  }

  u32(): number {
    return (this.u8() | (this.u8() << 8) | (this.u8() << 16) | (this.u8() << 24)) >>> 0;
  }

  slice(length: number): Uint8Array {
    if (length < 0 || this.offset + length > this.bytes.length) {
      throw new ReplayFormatError('replay truncated');
    }
    const view = this.bytes.subarray(this.offset, this.offset + length);
    this.offset += length;
    return view;
  }

  varint(): number {
    let result = 0;
    let shift = 0;
    for (;;) {
      const byte = this.u8();
      result |= (byte & 0x7f) << shift;
      if ((byte & 0x80) === 0) break;
      shift += 7;
      if (shift > 28) throw new ReplayFormatError('varint too long');
    }
    return result >>> 0;
  }
}

/* -------------------------------------------------------------- encode ---- */

export function encodeReplay(replay: Replay): Uint8Array {
  if (replay.header.pinSeed !== undefined && !isSeed(replay.header.pinSeed)) {
    throw new ReplayFormatError('invalid PIN seed');
  }
  if (!isSeed(replay.header.seed)) throw new ReplayFormatError('invalid replay seed');
  if (replay.frames.length > MAX_REPLAY_FRAMES) {
    throw new ReplayFormatError(`replay too long: ${replay.frames.length} frames`);
  }

  const headerBytes = new TextEncoder().encode(JSON.stringify(replay.header));
  if (headerBytes.length > MAX_HEADER_BYTES) {
    throw new ReplayFormatError('replay header too large');
  }

  const body: number[] = [];
  let previous: StepInput | null = null;
  let pendingRepeats = 0;

  const flushRepeats = (): void => {
    if (pendingRepeats === 0) return;
    body.push(TAG_REPEAT);
    writeVarint(body, pendingRepeats);
    pendingRepeats = 0;
  };

  for (const frame of replay.frames) {
    const buttons = frame.buttons & BUTTON_MASK;
    const yawQ = wrapYawUnits(frame.yawQ);
    const pitchQ = clampPitchUnits(frame.pitchQ);
    const repeatable = (buttons & EVENT_MASK) === 0;

    if (
      repeatable &&
      previous !== null &&
      previous.buttons === buttons &&
      previous.yawQ === yawQ &&
      previous.pitchQ === pitchQ
    ) {
      pendingRepeats++;
      continue;
    }

    flushRepeats();
    // The common frame is one byte. Anything the tag cannot hold - which is a
    // frame that pressed one of the three boxes - takes the escape and a
    // varint instead.
    if (buttons > FAST_BUTTON_MASK) {
      body.push(TAG_WIDE);
      writeVarint(body, buttons);
    } else {
      body.push(buttons);
    }
    // Yaw is wrapped, so the shortest way round is the honest delta.
    writeVarint(body, zigzag(wrapYawUnits(yawQ - (previous?.yawQ ?? 0))));
    writeVarint(body, zigzag(pitchQ - (previous?.pitchQ ?? 0)));
    previous = { buttons, yawQ, pitchQ };
  }
  flushRepeats();

  const out = new Uint8Array(4 + 2 + 2 + headerBytes.length + 4 + body.length);
  let offset = 0;
  for (const char of REPLAY_MAGIC) out[offset++] = char.charCodeAt(0);
  out[offset++] = REPLAY_VERSION & 0xff;
  out[offset++] = (REPLAY_VERSION >> 8) & 0xff;
  out[offset++] = headerBytes.length & 0xff;
  out[offset++] = (headerBytes.length >> 8) & 0xff;
  out.set(headerBytes, offset);
  offset += headerBytes.length;
  const count = replay.frames.length;
  out[offset++] = count & 0xff;
  out[offset++] = (count >> 8) & 0xff;
  out[offset++] = (count >> 16) & 0xff;
  out[offset++] = (count >> 24) & 0xff;
  out.set(body, offset);
  return out;
}

/* -------------------------------------------------------------- decode ---- */

function parseHeader(json: string): ReplayHeader {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new ReplayFormatError('replay header is not JSON');
  }
  if (!raw || typeof raw !== 'object') throw new ReplayFormatError('replay header is not an object');
  const value = raw as Partial<ReplayHeader>;

  const num = (input: unknown, name: string): number => {
    if (typeof input !== 'number' || !Number.isFinite(input)) {
      throw new ReplayFormatError(`replay header field "${name}" is not a finite number`);
    }
    return input;
  };
  const str = (input: unknown, name: string): string => {
    if (typeof input !== 'string') {
      throw new ReplayFormatError(`replay header field "${name}" is not a string`);
    }
    return input;
  };

  if (!isSeed(value.seed)) throw new ReplayFormatError('invalid replay seed');
  if (value.pinSeed !== undefined && !isSeed(value.pinSeed)) throw new ReplayFormatError('invalid PIN seed');

  return {
    ...(value.pinSeed === undefined ? {} : { pinSeed: value.pinSeed }),
    seed: value.seed,
    runId: str(value.runId ?? '', 'runId'),
    fingerprint: str(value.fingerprint, 'fingerprint'),
    levelName: str(value.levelName, 'levelName'),
    alcoholPromille: num(value.alcoholPromille, 'alcoholPromille'),
    drugsPercent: num(value.drugsPercent, 'drugsPercent'),
    simulationHz: num(value.simulationHz, 'simulationHz'),
    mouseSensitivity: num(value.mouseSensitivity, 'mouseSensitivity'),
    startedAt: num(value.startedAt ?? 0, 'startedAt'),
  };
}

export function decodeReplay(bytes: Uint8Array): Replay {
  const reader = new Reader(bytes);

  const magic = String.fromCharCode(...reader.slice(4));
  if (magic !== REPLAY_MAGIC) throw new ReplayFormatError('not a Laniaamu replay');

  const version = reader.u16();
  if (version !== REPLAY_VERSION) {
    throw new ReplayFormatError(`unsupported replay version ${version}`);
  }

  const headerLength = reader.u16();
  if (headerLength > MAX_HEADER_BYTES) throw new ReplayFormatError('replay header too large');
  const header = parseHeader(new TextDecoder().decode(reader.slice(headerLength)));

  const frameCount = reader.u32();
  if (frameCount > MAX_REPLAY_FRAMES) {
    throw new ReplayFormatError(`replay claims ${frameCount} frames`);
  }

  const frames: StepInput[] = [];
  let previous: StepInput | null = null;

  while (frames.length < frameCount) {
    const tag = reader.u8();

    if (tag === TAG_REPEAT) {
      if (previous === null) throw new ReplayFormatError('repeat before any frame');
      const repeats = reader.varint();
      if (repeats === 0 || frames.length + repeats > frameCount) {
        throw new ReplayFormatError('repeat count outside the frame count');
      }
      for (let i = 0; i < repeats; i++) frames.push({ ...previous });
      continue;
    }

    // The escape carries its own mask; every other tag *is* one.
    let buttons = tag;
    if (tag === TAG_WIDE) {
      buttons = reader.varint();
      // A wide frame that would have fitted in a tag byte is a second spelling
      // of a frame that already has one, and two spellings of one frame is a
      // way for two honest encoders to disagree about the same run.
      if (buttons <= FAST_BUTTON_MASK) throw new ReplayFormatError('escaped frame that fits a tag');
    } else if ((tag & 0x80) !== 0) {
      throw new ReplayFormatError('unknown frame tag');
    }

    if ((buttons & ~BUTTON_MASK) !== 0) throw new ReplayFormatError('unknown button bits');
    const yawQ = wrapYawUnits((previous?.yawQ ?? 0) + unzigzag(reader.varint()));
    const pitchQ = clampPitchUnits((previous?.pitchQ ?? 0) + unzigzag(reader.varint()));
    previous = { buttons, yawQ, pitchQ };
    frames.push({ ...previous });
  }

  // Trailing bytes mean the file does not say what it claims to say.
  if (reader.remaining !== 0) throw new ReplayFormatError('trailing bytes after the last frame');

  return { header, frames };
}
